import { OperatorOption } from '../constants';
import type { FilterInput } from '../constants';
import { isEbvPercentileField } from './ebvFilterFields';
import type { RangeCompositeValue } from './rangeValue';

const GTE = OperatorOption.greaterThanOrEqual.value;
const LTE = OperatorOption.lessThanOrEqual.value;

/** BREEDPLAN / i6 stored rank: 1 = best … 99 = worst (SQL tinyint). */
export const BREEDPLAN_PERCENTILE_MIN = 1;
export const BREEDPLAN_PERCENTILE_MAX = 99;

function parseRank(s: string): number | null {
  const n = Number.parseFloat(s.trim());
  return Number.isFinite(n) ? n : null;
}

function clampRank(n: number): number {
  return Math.max(
    BREEDPLAN_PERCENTILE_MIN,
    Math.min(BREEDPLAN_PERCENTILE_MAX, Math.round(n)),
  );
}

/**
 * On blur: keep empty; keep a single integer rank in 1–99; otherwise clear (invalid / out-of-range).
 */
export function normalizePercentileInputOnBlur(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n)) return '';
  const r = Math.round(n);
  if (r < BREEDPLAN_PERCENTILE_MIN || r > BREEDPLAN_PERCENTILE_MAX) {
    return '';
  }
  return String(r);
}

/**
 * BREEDPLAN / i6 percentile rank: 1 = best (top of breed), 99 = worst.
 * Expands min/max UI values into comparison filters.
 */
export function buildPercentilePairFilters(
  minStr: string,
  maxStr: string,
): { operator: string; value: string }[] {
  const minT = minStr.trim();
  const maxT = maxStr.trim();
  if (!minT && !maxT) return [];

  if (minT && maxT) {
    const a = parseRank(minT);
    const b = parseRank(maxT);
    if (a === null || b === null) return [];
    const lo = clampRank(Math.min(a, b));
    const hi = clampRank(Math.max(a, b));
    return [
      { operator: GTE, value: String(lo) },
      { operator: LTE, value: String(hi) },
    ];
  }

  if (minT && !maxT) {
    // "Min %" alone = top N% of the breed → ranks 1..N
    const n = parseRank(minT);
    if (n === null) return [];
    const hi = clampRank(n);
    return [
      { operator: GTE, value: '1' },
      { operator: LTE, value: String(hi) },
    ];
  }

  if (maxT) {
    const n = parseRank(maxT);
    if (n === null) return [];
    return [{ operator: LTE, value: String(clampRank(n)) }];
  }

  return [];
}

export function parsePercentileFiltersFromState(
  filters: FilterInput[],
  field: string,
): { minStr: string; maxStr: string } {
  const gte = filters.find((f) => f.field === field && f.operator === GTE);
  const lte = filters.find((f) => f.field === field && f.operator === LTE);
  if (gte && lte) {
    // Ranks 1..N (top N%) are stored as GTE(1)+LTE(N), same as an explicit band 1–N.
    if (gte.value.trim() === '1') {
      return { minStr: lte.value, maxStr: '' };
    }
    return { minStr: gte.value, maxStr: lte.value };
  }
  if (lte && !gte) {
    return { minStr: '', maxStr: lte.value };
  }
  if (gte && !lte) {
    return { minStr: gte.value, maxStr: '' };
  }
  return { minStr: '', maxStr: '' };
}

/** Old basic search sent orphan GTE on percentiles; treat as LTE (top N%). */
export function normalizeLegacyPercentileFilters(
  filters: FilterInput[],
): FilterInput[] {
  const percentileFields = new Set(
    filters.filter((f) => isEbvPercentileField(f.field)).map((f) => f.field),
  );
  const out = [...filters];
  for (const pf of percentileFields) {
    const idxGte = out.findIndex((f) => f.field === pf && f.operator === GTE);
    const idxLte = out.findIndex((f) => f.field === pf && f.operator === LTE);
    if (idxGte >= 0 && idxLte < 0) {
      out[idxGte] = { ...out[idxGte]!, operator: LTE };
    }
  }
  return out;
}

export function expandEbvCompositeForQuery(
  field: string,
  composite: RangeCompositeValue | null,
): { operator: string; value: string }[] {
  if (!composite) return [];
  const minT = composite.min?.trim() ?? '';
  const maxT = composite.max?.trim() ?? '';
  if (!isEbvPercentileField(field)) {
    const r: { operator: string; value: string }[] = [];
    if (minT) r.push({ operator: GTE, value: minT });
    if (maxT) r.push({ operator: LTE, value: maxT });
    return r;
  }
  return buildPercentilePairFilters(minT, maxT);
}
